import { useState } from "react";
import { AlarmClockPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ReminderPicker } from "./ReminderPicker";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";

/**
 * Set a reminder from anywhere, without first navigating to a company.
 *
 * Until now the only door to ReminderPicker on desktop was the bell on an
 * entity's detail page, so setting a reminder that had nothing to do with a
 * company meant opening one anyway. The Tasks page's quick-add covers planned
 * work; this covers the "remind me about this" thought you have mid-flow.
 *
 * The icon is an alarm clock, deliberately not a bell: the bell two positions
 * along is notifications, and two bells in one toolbar would mean two different
 * things.
 */
export function NewReminderButton() {
  const [open, setOpen] = useState(false);
  const userTimezone = useCurrentUserTimezone();

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOpen(true)}
            aria-label="New reminder"
            data-testid="new-reminder-button"
            className="text-muted-foreground hover:text-foreground"
          >
            <AlarmClockPlus className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>New reminder</TooltipContent>
      </Tooltip>

      {/* No entity defaults: this one is not about anything in particular. */}
      <ReminderPicker open={open} onOpenChange={setOpen} userTimezone={userTimezone} />
    </>
  );
}
